import { addDays, addMonths, addYears } from "date-fns";
import {
  EmployeeExitType,
  ExerciseWindowUnit,
  ExitDateBasis,
  ExitForfeitureRule,
  ForfeitureSettings,
  ExerciseWindow,
} from "../types/EsopPlan";

/** Describes the outcome of comparing the basis-date expiry against the other exit date. */
export type DualDateGuardInfo = {
  resolution: "EARLIER_DATE" | "LATER_DATE";
  /** Expiry computed from the configured exit date basis (e.g. resignation date + offset). */
  basisDate: Date;
  basisLabel: string;
  /** The other, un-offset exit date the basis date is compared against (e.g. last working date). */
  otherDate: Date;
  otherLabel: string;
  effectiveDate: Date;
  usedOther: boolean;
};

/** Applies settings.dualDateGuard (if enabled) to an already-resolved expiry date. */
function applyDualDateGuard(
  resolvedExpiryDate: Date | undefined,
  otherDate: Date | null | undefined,
  settings: ForfeitureSettings
): Date | undefined {
  return computeDualDateGuard(resolvedExpiryDate, otherDate, settings)
    .effectiveDate;
}

/**
 * Same comparison as `applyDualDateGuard`, but also returns the metadata needed
 * to explain the earlier/later decision to the user (both candidate dates and
 * which one was used).
 */
function computeDualDateGuard(
  resolvedExpiryDate: Date | undefined,
  otherDate: Date | null | undefined,
  settings: ForfeitureSettings
): { effectiveDate: Date | undefined; info: DualDateGuardInfo | null } {
  const guard = settings.dualDateGuard;
  if (!guard?.enabled || !resolvedExpiryDate || !otherDate) {
    return { effectiveDate: resolvedExpiryDate, info: null };
  }
  const usedOther =
    guard.resolution === "EARLIER_DATE"
      ? otherDate.getTime() < resolvedExpiryDate.getTime()
      : otherDate.getTime() > resolvedExpiryDate.getTime();
  const basis = settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE;
  const basisLabel =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? "Resignation date + offset"
      : "Last working date + offset";
  const otherLabel =
    basis === ExitDateBasis.RESIGNATION_DATE
      ? "Last working date"
      : "Resignation date";
  const effectiveDate = usedOther ? otherDate : resolvedExpiryDate;
  return {
    effectiveDate,
    info: {
      resolution: guard.resolution,
      basisDate: resolvedExpiryDate,
      basisLabel,
      otherDate,
      otherLabel,
      effectiveDate,
      usedOther,
    },
  };
}

function getGuardDate(
  employeeDates: { resignationDate?: Date | null; lastDay?: Date | null },
  settings: ForfeitureSettings
): Date | null | undefined {
  const basis = settings.exitDateBasis ?? ExitDateBasis.LAST_WORKING_DATE;
  if (basis !== ExitDateBasis.RESIGNATION_DATE) return undefined;
  return employeeDates.lastDay;
}

// ─── Map separation reason strings to EmployeeExitType ───────────────────────

export function mapReasonToExitType(
  reason?: string
): EmployeeExitType | undefined {
  if (!reason) return undefined;
  const upper = reason.trim().toUpperCase();
  if (upper === "RESIGNATION") return EmployeeExitType.RESIGNATION;
  if (upper === "TERMINATION") return EmployeeExitType.TERMINATION;
  if (upper === "RETIREMENT") return EmployeeExitType.RETIREMENT;
  if (upper === "PERFORMANCE_RELATED")
    return EmployeeExitType.PERFORMANCE_RELATED;
  if (upper === "MISCONDUCT") return EmployeeExitType.MISCONDUCT;
  if (upper === "PERMANENT_DISABILITY" || upper.includes("DISAB"))
    return EmployeeExitType.PERMANENT_DISABILITY;
  if (upper === "DEATH" || upper.includes("DECEAS"))
    return EmployeeExitType.DEATH;
  if (upper === "ABANDONMENT") return EmployeeExitType.ABANDONMENT;

  // Partial matches
  if (upper.includes("TERMINAT")) return EmployeeExitType.TERMINATION;
  if (upper.includes("RETIR")) return EmployeeExitType.RETIREMENT;
  if (upper.includes("MISCONDUCT")) return EmployeeExitType.MISCONDUCT;
  if (upper.includes("PERFORMANCE"))
    return EmployeeExitType.PERFORMANCE_RELATED;
  if (upper.includes("ABANDON")) return EmployeeExitType.ABANDONMENT;

  return EmployeeExitType.RESIGNATION;
}

// ─── Look up the exit rule for a given exit type ─────────────────────────────

export function getExitRule(
  settings: ForfeitureSettings,
  exitType: EmployeeExitType
): ExitForfeitureRule | undefined {
  return settings.exitRules.find((r) => r.exitType === exitType);
}

// ─── Compute last date to exercise ──────────────────────────────────────────

/**
 * Frontend mirror of the backend `getLastDateToExerciseFromPlan`.
 * Returns the date until which the employee can exercise their vested options,
 * or `undefined` if the window never expires or no applicable rule exists.
 */
export function computeLastDateToExercise(
  lastDay: Date,
  settings: ForfeitureSettings,
  reason?: string,
  resignationDate?: Date | null
): Date | undefined {
  const exitType = mapReasonToExitType(reason);
  if (!exitType) return undefined;

  const rule = getExitRule(settings, exitType);
  if (!rule) return undefined;

  const { exerciseWindow } = rule.vestedOptions;
  if (exerciseWindow.neverExpires) return undefined;

  const { value, unit } = exerciseWindow;
  let resolvedExpiryDate: Date;
  switch (unit) {
    case ExerciseWindowUnit.DAYS:
      resolvedExpiryDate = addDays(lastDay, value);
      break;
    case ExerciseWindowUnit.MONTHS:
      resolvedExpiryDate = addMonths(lastDay, value);
      break;
    case ExerciseWindowUnit.YEARS:
      resolvedExpiryDate = addYears(lastDay, value);
      break;
    default:
      resolvedExpiryDate = addDays(lastDay, value);
  }

  return applyDualDateGuard(
    resolvedExpiryDate,
    getGuardDate({ lastDay, resignationDate }, settings),
    settings
  );
}

// ─── Format exercise window for display ─────────────────────────────────────

export function formatExerciseWindow(window: {
  value: number;
  unit: ExerciseWindowUnit;
  neverExpires?: boolean;
}): string {
  if (window.neverExpires) return "Never expires";
  const unitLabel =
    window.value === 1
      ? window.unit.charAt(0) +
        window.unit.slice(1).toLowerCase().replace(/s$/, "")
      : window.unit.charAt(0) + window.unit.slice(1).toLowerCase();
  return `${window.value} ${unitLabel}`;
}

// ─── Human-readable label for expiry mode ──────────────────────────────────

export function formatExpiryMode(
  activeEmployee: ForfeitureSettings["activeEmployee"]
): string {
  const val = activeEmployee.expiryValue ?? "?";
  const unit = (activeEmployee.expiryUnit ?? "MONTHS").toLowerCase();
  switch (activeEmployee.expiryMode) {
    case "GRANT_EXPIRY":
      return `Expires ${val} ${unit} from grant date`;
    case "FROM_VESTING_DATE":
      return `Expires ${val} ${unit} from vesting date`;
    case "NEVER":
      return "Never expires";
    default:
      return activeEmployee.expiryMode;
  }
}

export function describeActiveEmployeeExpiry(
  activeEmployee: ForfeitureSettings["activeEmployee"]
): string {
  const val = activeEmployee.expiryValue ?? "?";
  const unit = (activeEmployee.expiryUnit ?? "MONTHS").toLowerCase();
  switch (activeEmployee.expiryMode) {
    case "GRANT_EXPIRY":
      return `Grants expire ${val} ${unit} from the original grant date while the employee remains active.`;
    case "FROM_VESTING_DATE":
      return `Each vested tranche expires ${val} ${unit} from its vesting date while the employee remains active.`;
    case "NEVER":
      return "Grants do not expire while the employee is active.";
    case "SPECIFIC_DATE":
      return activeEmployee.specificDate
        ? `Grants expire on ${activeEmployee.specificDate} for all active employees.`
        : "Grants expire on a specific date for all active employees.";
    default:
      return "No expiry policy configured.";
  }
}

// ─── Tenure-based Exit Outcome Resolution ────────────────────────────────────

type ResolveEmployee = {
  dateOfJoining: Date;
  lastDay?: Date | null;
  resignationDate?: Date | null;
};

type ResolvedOutcome = {
  vestedOptions: ExitForfeitureRule["vestedOptions"];
  unvestedOptions: ExitForfeitureRule["unvestedOptions"];
  resolvedExpiryDate: Date | undefined;
  dualDateGuardInfo: DualDateGuardInfo | null;
  appliedRule: {
    vested: "DEFAULT" | "CONDITIONAL";
    unvested: "DEFAULT" | "CONDITIONAL";
  };
};

export function monthsBetween(from: Date, to: Date): number {
  const years = to.getFullYear() - from.getFullYear();
  const months = to.getMonth() - from.getMonth();
  return years * 12 + months;
}

function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

function tenureInUnit(from: Date, to: Date, unit?: ExerciseWindowUnit): number {
  switch (unit) {
    case ExerciseWindowUnit.DAYS:
      return daysBetween(from, to);
    case ExerciseWindowUnit.YEARS:
      return Math.floor(monthsBetween(from, to) / 12);
    default:
      return monthsBetween(from, to);
  }
}

export function resolveExitOutcome(
  reasonForTermination: string | null | undefined,
  anchorDate: Date,
  employee: ResolveEmployee,
  settings: ForfeitureSettings
): ResolvedOutcome {
  const exitType =
    mapReasonToExitType(reasonForTermination || undefined) ||
    EmployeeExitType.RESIGNATION;
  const rule =
    settings.exitRules.find((r) => r.exitType === exitType) ??
    settings.exitRules.find((r) => r.exitType === EmployeeExitType.RESIGNATION);

  if (!rule) {
    return {
      vestedOptions: {
        exerciseWindow: { value: 0, unit: ExerciseWindowUnit.DAYS },
        onExpiry: "LAPSE" as any,
      },
      unvestedOptions: { forfeitImmediately: true },
      resolvedExpiryDate: undefined,
      dualDateGuardInfo: null,
      appliedRule: { vested: "DEFAULT", unvested: "DEFAULT" },
    };
  }

  const guardDate = getGuardDate(employee, settings);

  if (!rule.conditionalRules || rule.conditionalRules.length === 0) {
    const guardResult = rule.vestedOptions.exerciseWindow.neverExpires
      ? { effectiveDate: undefined, info: null }
      : computeDualDateGuard(
          computeExpiryFromWindow(
            rule.vestedOptions.exerciseWindow,
            anchorDate
          ),
          guardDate,
          settings
        );
    return {
      vestedOptions: rule.vestedOptions,
      unvestedOptions: rule.unvestedOptions,
      resolvedExpiryDate: guardResult.effectiveDate,
      dualDateGuardInfo: guardResult.info,
      appliedRule: { vested: "DEFAULT", unvested: "DEFAULT" },
    };
  }

  const referenceDate = employee.lastDay ?? new Date();

  const matchedConditionals = rule.conditionalRules.filter((cg) => {
    if (!cg.tenure) return true;
    const tenure = tenureInUnit(
      employee.dateOfJoining,
      referenceDate,
      cg.tenure.unit
    );
    if (tenure < cg.tenure.minMonths) return false;
    if (cg.tenure.maxMonths !== undefined && tenure > cg.tenure.maxMonths)
      return false;
    return true;
  });

  if (matchedConditionals.length === 0) {
    const guardResult = rule.vestedOptions.exerciseWindow.neverExpires
      ? { effectiveDate: undefined, info: null }
      : computeDualDateGuard(
          computeExpiryFromWindow(
            rule.vestedOptions.exerciseWindow,
            anchorDate
          ),
          guardDate,
          settings
        );
    return {
      vestedOptions: rule.vestedOptions,
      unvestedOptions: rule.unvestedOptions,
      resolvedExpiryDate: guardResult.effectiveDate,
      dualDateGuardInfo: guardResult.info,
      appliedRule: { vested: "DEFAULT", unvested: "DEFAULT" },
    };
  }

  const winningConditional = pickWinningConditional(
    matchedConditionals,
    rule.conditionalResolution,
    anchorDate
  );

  const resolvedVested =
    winningConditional?.vestedOptions ?? rule.vestedOptions;
  const resolvedUnvested =
    winningConditional?.unvestedOptions ?? rule.unvestedOptions;

  const guardResult = resolvedVested.exerciseWindow.neverExpires
    ? { effectiveDate: undefined, info: null }
    : computeDualDateGuard(
        computeExpiryFromWindow(resolvedVested.exerciseWindow, anchorDate),
        guardDate,
        settings
      );

  return {
    vestedOptions: resolvedVested,
    unvestedOptions: resolvedUnvested,
    resolvedExpiryDate: guardResult.effectiveDate,
    dualDateGuardInfo: guardResult.info,
    appliedRule: {
      vested: winningConditional?.vestedOptions ? "CONDITIONAL" : "DEFAULT",
      unvested: winningConditional?.unvestedOptions ? "CONDITIONAL" : "DEFAULT",
    },
  };
}

function computeExpiryFromWindow(
  window: ExerciseWindow,
  anchorDate: Date
): Date {
  if (window.unit === ExerciseWindowUnit.DAYS)
    return addDays(anchorDate, window.value ?? 0);
  if (window.unit === ExerciseWindowUnit.YEARS)
    return addYears(anchorDate, window.value ?? 0);
  return addMonths(anchorDate, window.value ?? 0);
}

function pickWinningConditional(
  matchedConditionals: any[],
  resolution: string | undefined,
  anchorDate: Date
): any {
  if (resolution !== "EARLIER_DATE" && resolution !== "LATER_DATE") {
    return matchedConditionals[0];
  }

  const candidatesWithVested = matchedConditionals.filter(
    (rule) => !!rule.vestedOptions
  );

  if (candidatesWithVested.length === 0) {
    return matchedConditionals[0];
  }

  let winner = candidatesWithVested[0];
  let winnerExpiry = getWindowExpiryOrInfinite(
    winner.vestedOptions!.exerciseWindow,
    anchorDate
  );

  for (const candidate of candidatesWithVested.slice(1)) {
    const candidateExpiry = getWindowExpiryOrInfinite(
      candidate.vestedOptions!.exerciseWindow,
      anchorDate
    );

    const shouldReplaceWinner =
      resolution === "EARLIER_DATE"
        ? candidateExpiry.getTime() < winnerExpiry.getTime()
        : candidateExpiry.getTime() > winnerExpiry.getTime();

    if (shouldReplaceWinner) {
      winner = candidate;
      winnerExpiry = candidateExpiry;
    }
  }

  return winner;
}

function getWindowExpiryOrInfinite(
  window: ExerciseWindow,
  anchorDate: Date
): Date {
  if (window.neverExpires) {
    return new Date(8640000000000000);
  }

  return computeExpiryFromWindow(window, anchorDate);
}
